import { useState } from 'react';
import type { AppSession, Feeling, PatientView, ReadingContext, Symptom } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { BP_CLASS, classifyBp, fmtBp, fmtDateTime } from '../../lib/format';
import { Icon } from '../../components/HeartMark';
import { Chip } from '../../components/Bits';

const FEELINGS: Array<[Feeling, string]> = [
  ['great', 'Great'],
  ['good', 'Good'],
  ['okay', 'Okay'],
  ['tired', 'Tired'],
  ['dizzy', 'Dizzy'],
];
const CONTEXTS: Array<[ReadingContext, string]> = [
  ['just-woke-up', 'Just woke up'],
  ['after-exercise', 'After exercise'],
  ['after-eating', 'After eating'],
  ['resting', 'Resting'],
  ['before-bed', 'Before bed'],
];
const SYMPTOMS: Array<[Symptom, string]> = [
  ['none', 'None'],
  ['headache', 'Headache'],
  ['chest-tightness', 'Chest tightness'],
  ['shortness-of-breath', 'Shortness of breath'],
  ['heart-racing', 'Heart racing'],
];

type Done = { systolic: number; diastolic: number; earned: number; insight: string | null; view: PatientView };

/** The one-tap readings: the patient's own weekly average first, then two reference points. */
function presets(view: PatientView): Array<[number, number, string]> {
  const out: Array<[number, number, string]> = [];
  if (view.weeklyAverage) out.push([view.weeklyAverage.systolic, view.weeklyAverage.diastolic, 'My average']);
  out.push([120, 80, 'Normal'], [130, 85, 'Elevated']);
  return out;
}

/** Logging a reading: a modal over the page, in three steps. Closing at any point logs nothing. */
export function BpLogFlow({ session, view, onDone, say, onClose }: { session: AppSession; view: PatientView; onDone: () => Promise<void>; say: (s: string) => void; onClose: () => void }) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [systolic, setSystolic] = useState('');
  const [diastolic, setDiastolic] = useState('');
  const [feeling, setFeeling] = useState<Feeling>('good');
  const [context, setContext] = useState<ReadingContext | null>(null);
  const [symptoms, setSymptoms] = useState<Symptom[]>(['none']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  const s = Number(systolic);
  const d = Number(diastolic);
  const valid = Number.isInteger(s) && Number.isInteger(d) && s >= 60 && s <= 260 && d >= 30 && d <= 160 && d < s;

  const toggleSymptom = (sym: Symptom) => {
    if (sym === 'none') return setSymptoms(['none']);
    const without = symptoms.filter((x) => x !== 'none' && x !== sym);
    setSymptoms(symptoms.includes(sym) ? (without.length ? without : ['none']) : [...without, sym]);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.postEvent({ type: 'log-bp', systolic: s, diastolic: d, feeling, ...(context ? { context } : {}), symptoms }, session.token);
      setDone({ systolic: s, diastolic: d, earned: r.earned.reduce((n, e) => n + e.coins, 0), insight: r.insight, view: r.view });
      setStep(2);
      await onDone();
      if (symptoms.some((x) => x === 'chest-tightness' || x === 'shortness-of-breath')) say('Your care team has been flagged about these symptoms. If they are severe, call emergency services.');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not log the reading.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Log a blood pressure reading" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        {step === 2 && done ? <Result done={done} onClose={onClose} /> : null}

        {step < 2 ? (
          <>
            <header className="modal-head">
              <button type="button" className="icon-btn modal-close" onClick={onClose} aria-label="Close">
                <Icon name="close" />
              </button>
              <span className="eyebrow">Blood pressure log</span>
              <h2>{step === 0 ? 'Enter your reading' : 'A bit more context'}</h2>
              <span className="hint">{step === 0 ? 'Use your cuff or a connected device' : 'Helps your coach give better insights'}</span>
            </header>

            <div className="modal-body">
              {step === 0 ? (
                <>
                  <div className="reading-entry">
                    <label className="reading-field">
                      <span className="eyebrow">Systolic</span>
                      <small>upper number</small>
                      <input inputMode="numeric" pattern="[0-9]*" value={systolic} placeholder="120" maxLength={3} onChange={(e) => setSystolic(e.target.value.replace(/\D/g, ''))} autoFocus aria-label="Systolic" />
                      <small>mmHg</small>
                    </label>
                    <span className="slash" aria-hidden="true">
                      /
                    </span>
                    <label className="reading-field">
                      <span className="eyebrow">Diastolic</span>
                      <small>lower number</small>
                      <input inputMode="numeric" pattern="[0-9]*" value={diastolic} placeholder="80" maxLength={3} onChange={(e) => setDiastolic(e.target.value.replace(/\D/g, ''))} aria-label="Diastolic" />
                      <small>mmHg</small>
                    </label>
                  </div>

                  <h3>Common readings</h3>
                  <div className="presets">
                    {presets(view).map(([sy, di, label]) => (
                      <button key={label} type="button" className="preset" onClick={() => (setSystolic(String(sy)), setDiastolic(String(di)))}>
                        <strong>
                          {sy}/{di}
                        </strong>
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>

                  <h3>Blood pressure reference</h3>
                  <ul className="ref">
                    <li>
                      <i className="dot" /> Optimal <span>&lt; 120/80</span>
                    </li>
                    <li>
                      <i className="dot warn" /> Elevated <span>120-129 / &lt; 80</span>
                    </li>
                    <li>
                      <i className="dot orange" /> High Stage 1 <span>130-139 / 80-89</span>
                    </li>
                    <li>
                      <i className="dot bad" /> High Stage 2 <span>≥ 140 / ≥ 90</span>
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  <h3>How are you feeling?</h3>
                  <div className="chips">
                    {FEELINGS.map(([v, label]) => (
                      <Chip key={v} on={feeling === v} onClick={() => setFeeling(v)}>
                        {label}
                      </Chip>
                    ))}
                  </div>
                  <h3>When did you take it?</h3>
                  <div className="chips">
                    {CONTEXTS.map(([v, label]) => (
                      <Chip key={v} on={context === v} onClick={() => setContext(context === v ? null : v)}>
                        {label}
                      </Chip>
                    ))}
                  </div>
                  <h3>Any symptoms?</h3>
                  <div className="chips">
                    {SYMPTOMS.map(([v, label]) => (
                      <Chip key={v} on={symptoms.includes(v)} onClick={() => toggleSymptom(v)}>
                        {label}
                      </Chip>
                    ))}
                  </div>
                  {error ? <p className="form-error">{error}</p> : null}
                </>
              )}
            </div>

            <footer className="modal-foot">
              {step === 0 ? (
                <>
                  <button type="button" className="quiet" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="button" className="primary" disabled={!valid} onClick={() => setStep(1)}>
                    Continue →
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="quiet" onClick={() => setStep(0)}>
                    Back
                  </button>
                  <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
                    {busy ? 'Logging…' : 'Log reading ✓'}
                  </button>
                </>
              )}
            </footer>
          </>
        ) : null}
      </div>
    </div>
  );
}

function Result({ done, onClose }: { done: Done; onClose: () => void }) {
  const cls = BP_CLASS[classifyBp(done.systolic, done.diastolic)];
  const trend = done.view.trend.slice(-5);
  return (
    <>
      <header className="modal-head center result-hero">
        <button type="button" className="icon-btn modal-close" onClick={onClose} aria-label="Close">
          <Icon name="close" />
        </button>
        <span className="eyebrow">Logged successfully</span>
        <strong className="huge" style={{ marginTop: '0.5rem' }}>
          {done.systolic}/{done.diastolic}
        </strong>
        <span className="hint">
          mmHg · {cls?.label}
        </span>
      </header>
      <div className="modal-body">
        {done.earned > 0 ? (
          <div className="earned-strip">
            <span className="coin" aria-hidden="true">
              <Icon name="coin" size={22} />
            </span>
            <span className="row-main">
              <strong>+{done.earned} HeartCoins earned</strong>
              <span className="hint">For logging your blood pressure today</span>
            </span>
          </div>
        ) : (
          <p className="hint">You had already earned today's reading coins. Every reading still counts toward your week.</p>
        )}

        <div className="card" style={{ background: 'var(--surface-2)' }}>
          <span className="eyebrow">
            <Icon name="chat" size={13} /> Coach insight
          </span>
          <p style={{ marginTop: '0.5rem' }}>{done.insight}</p>
        </div>

        <h3 style={{ marginTop: '1.2rem' }}>This week's trend</h3>
        <div className="trend-row">
          {trend.map((t, i) => (
            <div key={i} className={`trend-cell ${i === trend.length - 1 ? 'today' : ''}`}>
              <span className="hint small">{i === trend.length - 1 ? 'Today' : t.label}</span>
              <strong>{fmtBp(t.reading)}</strong>
            </div>
          ))}
        </div>

        {done.view.outreach.optedOut || done.view.outreach.nextCheckInAt === null ? null : (
          <p className="reminder">
            <Icon name="bell" size={16} /> Your next check-in is {fmtDateTime(done.view.outreach.nextCheckInAt)}
          </p>
        )}
      </div>
      <footer className="modal-foot">
        <button type="button" className="primary" onClick={onClose}>
          Back to home
        </button>
      </footer>
    </>
  );
}
